/**
 * The physical network plant of one site — rooms, racks, rack units, the
 * links between them (migration 20260928120000) and the named networks that
 * run over them (20260928150000). Staff-only tables; the admin
 * session reads them under `is_paznetworks_staff()`.
 *
 * Scoped by location explicitly rather than trusting RLS, because staff can
 * read every site's rows.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import {
  buildRackPlant,
  type NetworkMemberRow,
  type RackLinkRow,
  type RackPlant,
  type RackRow,
  type RackUnitRow,
  type RoomRow,
  type SiteNetworkRow,
} from '../lib/rackModel';

const ROOM_COLUMNS = 'id, location_id, code, name, role, floor, description, note, data_as_of, source, sort_order';
const RACK_COLUMNS = 'id, room_id, code, name, height_u, height_captured, numbering, note, sort_order';
const UNIT_COLUMNS =
  'id, rack_id, asset_id, face, ru_start, ru_height, label, detail, kind, confidence, ip_address, owner, check_note';
/** The unit's surveyed MAC, and its linked asset's MAC and IP as fallbacks. */
const ADDRESS_COLUMNS = 'id, mac_address, asset:assets(mac_address, ip_address)';
type AssetAddress = { mac_address: string | null; ip_address: unknown };
type AddressRow = { id: string; mac_address: unknown; asset: AssetAddress | AssetAddress[] | null };
const LINK_COLUMNS = 'id, from_unit_id, to_unit_id, to_rack_id, medium, label, note, confidence, network_id';
const NETWORK_COLUMNS = 'id, location_id, code, name, owner, kind, color_token, description, source, sort_order';
const MEMBER_COLUMNS = 'id, network_id, unit_id, room_id, role, confidence, note';

export function useRackPlant(locationId: string | undefined) {
  return useQuery<RackPlant>({
    queryKey: ['admin-rack-plant', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      const rooms = await supabase
        .from('network_rooms')
        .select(ROOM_COLUMNS)
        .eq('location_id', locationId!)
        .is('deleted_at', null);
      if (rooms.error) throw rooms.error;
      const roomIds = (rooms.data ?? []).map((r) => r.id);
      if (!roomIds.length) return buildRackPlant({ rooms: [], racks: [], units: [], links: [] });

      const racks = await supabase.from('racks').select(RACK_COLUMNS).in('room_id', roomIds).is('deleted_at', null);
      if (racks.error) throw racks.error;
      const rackIds = (racks.data ?? []).map((r) => r.id);

      const units = rackIds.length
        ? await supabase.from('rack_units').select(UNIT_COLUMNS).in('rack_id', rackIds)
        : { data: [], error: null };
      if (units.error) throw units.error;
      const unitIds = (units.data ?? []).map((u) => u.id);

      // Addresses recorded anywhere we hold them: the survey's own MAC, and
      // the linked assets row's MAC and IP when the survey has none. Read
      // separately and tolerated when missing, because an address is a label
      // detail, not something the explorer should fail over.
      const macs = new Map<string, string>();
      const assetIps = new Map<string, string>();
      if (unitIds.length) {
        const res = await supabase.from('rack_units').select(ADDRESS_COLUMNS).in('id', unitIds);
        for (const row of (res.error ? [] : res.data ?? []) as unknown as AddressRow[]) {
          const asset = Array.isArray(row.asset) ? row.asset[0] : row.asset;
          const mac = row.mac_address ?? asset?.mac_address ?? null;
          if (mac) macs.set(row.id, String(mac));
          if (asset?.ip_address) assetIps.set(row.id, String(asset.ip_address));
        }
      }

      const links = unitIds.length
        ? await supabase.from('rack_links').select(LINK_COLUMNS).in('from_unit_id', unitIds)
        : { data: [], error: null };
      if (links.error) throw links.error;

      const networks = await supabase
        .from('site_networks')
        .select(NETWORK_COLUMNS)
        .eq('location_id', locationId!)
        .is('deleted_at', null);
      if (networks.error) throw networks.error;
      const networkIds = (networks.data ?? []).map((n) => n.id);
      const members = networkIds.length
        ? await supabase.from('site_network_members').select(MEMBER_COLUMNS).in('network_id', networkIds)
        : { data: [], error: null };
      if (members.error) throw members.error;

      return buildRackPlant({
        rooms: (rooms.data ?? []) as RoomRow[],
        racks: (racks.data ?? []) as RackRow[],
        units: (units.data ?? []).map((u) => ({
          ...u,
          ip_address: (u.ip_address as string | null) ?? assetIps.get(u.id) ?? null,
          mac_address: macs.get(u.id) ?? null,
        })) as RackUnitRow[],
        links: (links.data ?? []) as RackLinkRow[],
        networks: (networks.data ?? []) as SiteNetworkRow[],
        members: (members.data ?? []) as NetworkMemberRow[],
      });
    },
  });
}
